import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import isArray from 'lodash/isArray';
import transform from 'lodash/transform';
import set from 'lodash/set'; // Remove all top level attributes
// that are null & requesting cleanup
// NOTE: creates side effects

export function deleteNullValues(obj) {
  if (typeof obj !== 'object') return;
  Object.keys(obj).forEach(key => {
    if (obj[key] === null) {
      delete obj[key];
    }
  });
} // Remove all top level attributes
// that are null & requesting cleanup
// NOTE: creates side effects

export function deleteUndefinedValues(obj) {
  if (typeof obj !== 'object') return;
  Object.keys(obj).forEach(key => {
    if (obj[key] === undefined) {
      delete obj[key];
    }
  });
  return obj;
} // Remove all top level attrs
// that are non-truthy

export function deleteNotTruthey(obj) {
  if (typeof obj !== 'object') return;
  Object.keys(obj).forEach(key => {
    if (!obj[key]) {
      delete obj[key];
    }
  });
  return obj;
} // Remove all empty objects from an object
// returning null if object itself is empty

export function deleteEmptyObjects(src) {
  if (!src || typeof src !== 'object') return src;
  const obj = cloneDeep(src);
  Object.keys(obj).forEach(key => {
    if (typeof obj[key] === 'object' && Object.keys(obj[key]).length === 0) {
      delete obj[key];
    }
  });
  return Object.keys(obj).length > 0 ? obj : null;
} // Lookup the hash key of a
// reference item within the hash

export function getHashItemKey(obj, target) {
  return Object.keys(obj).find(key => obj[key] === target) || '';
} // Deep difference between two objects
// returns a new object that contains only
// the changes from the base object.

export function getObjectDiff(object, base) {
  let replaceArrays = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  function changes(object, base) {
    return transform(object, function (result, value, key) {
      if (!isEqual(value, base[key])) {
        if (replaceArrays && isArray(value)) {
          // Return the entire array if it differs from base
          result[key] = [...value];
        } else if (isObject(value) && isObject(base[key])) {
          const nestedChanges = changes(value, base[key]); // Only include nested changes if the result is not empty

          if (Object.keys(nestedChanges).length > 0) {
            result[key] = nestedChanges;
          }
        } else {
          result[key] = value;
        }
      }
    });
  }

  return changes(object, base);
} // Flatten an object into a single level object
// with keys representing the path to the value.

export function flattenObject(obj) {
  let prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  let separator = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '.';
  let sufix = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '';
  return Object.keys(obj || {}).reduce((acc, key) => {
    const value = obj[key];
    let newKey = key;
    if (prefix) newKey = `${prefix}${separator}${key}`;
    if (sufix) newKey = `${key}${separator}${sufix}`;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(acc, flattenObject(value, newKey, separator, sufix));
    } else {
      acc[newKey] = value;
    }

    return acc;
  }, {});
} // Converts a flattened object with path-like keys back into a nested object.

export function unflattenObject(flatObj) {
  let separator = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '.';
  const result = {};

  for (const flatKey in flatObj) {
    const path = flatKey.split(separator);
    set(result, path, flatObj[flatKey]);
  }

  return result;
} // A customizer for _.mergeWith that replaces primitive arrays instead of merging them.

export function arrayOverwriteStrategy(_, srcValue) {
  if (isArray(srcValue)) {
    // By default, mergeWith merges array elements. We override this for
    // primitive arrays by checking if the array contains any objects.
    const isArrayOfObjects = srcValue.some(isPlainObject);

    if (!isArrayOfObjects) {
      return srcValue;
    }
  } // Let Lodash handle the default merge behavior


  return undefined;
} // Extracts all values associated with a specific key from a deeply nested object

export function findValuesByKey(obj, targetKeys) {
  const results = [];
  const keysSet = new Set(Array.isArray(targetKeys) ? targetKeys : [targetKeys]);

  function traverse(currentObj) {
    // Early return if not an object or is null
    if (typeof currentObj !== 'object' || currentObj === null) {
      return;
    } // Cast currentObj type


    const objToTraverse = currentObj;

    for (const [key, value] of Object.entries(objToTraverse)) {
      // If the current key matches the target key, add its value to results
      if (keysSet.has(key)) {
        results.push(value);
      } // Recursively traverse nested objects


      if (typeof value === 'object' && value !== null) {
        traverse(value);
      }
    }
  }

  traverse(obj);
  return results;
}